import {
  Client,
  ClientRepresentationStatusEnum,
  PermissionEnum,
  WorkspaceTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchClientsPaginated } from '../../../api/workspaces/clientApi';
import { UNCONFIRMED_CLIENTS_FETCH_LIMIT } from '../../../components/ConfirmClients';
import AppLoader from '../../../components/Loader';
import { useCurrentUserAuth } from '../../../hooks/useCurrentUserAuth';
import { reduxServices } from '../../../reducers/reduxServices';
import { useAppDispatch, useTypedSelector } from '../../../stateStore';
import { AuthHelper } from '../../../utils/auth/authHelper';
import { isLoadingState } from '../../../utils/helpers/common';
import {
  ConfirmClientsModal,
  UnconfirmedClientsAlert,
} from './ConfirmClientsPrompt';

type TUnconfirmedClients = {
  workspaceClients: Client[] | undefined;
  workspaceId?: string | undefined;
  workspaceName?: string | undefined;
  workspaceType?: WorkspaceTypeEnum | undefined;
}[];

export const AlertConfirmClients = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { currentUser } = useCurrentUserAuth();

  const workspaces = useTypedSelector(
    reduxServices.workspace.selectors.workspaces,
  );
  const isWorkspaceLoading = useTypedSelector(
    reduxServices.workspace.selectors.isLoading,
  );

  const [fetching, setFetching] = useState(false);
  const [showConfirmClientsModal, setShowConfirmClientsModal] = useState(false);
  const [reviewClients, setReviewClients] = useState<Client[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [unconfirmedClientsByWorkspace, setUnconfirmedClientsByWorkspace] =
    useState<TUnconfirmedClients>([]);

  const getAllWorkspaceClients = async () => {
    if (!currentUser) return;
    setFetching(true);

    const workspaceIds = AuthHelper.forUser(currentUser).getWorkspaceIds(
      PermissionEnum.VerifyClientRepresentation,
    );

    const clientByWorkspacePromises = workspaceIds.map(
      async (workspaceId: string | undefined) => {
        const workspaceClients = await fetchClientsPaginated({
          pageSize: UNCONFIRMED_CLIENTS_FETCH_LIMIT,
          representationStatus: ClientRepresentationStatusEnum.Unconfirmed,
          workspaceId: workspaceId ?? '',
        });

        if (!workspaceClients.items.length) return;

        const foundWorkspace = workspaces.find(
          (workspace) => workspace._id === workspaceId,
        );

        if (!foundWorkspace) return;
        if (foundWorkspace.workspaceType !== WorkspaceTypeEnum.BusinessAgency) {
          return;
        }

        return {
          workspaceClients: workspaceClients.items,
          workspaceId: workspaceId,
          workspaceName: foundWorkspace?.workspaceName,
          workspaceType: foundWorkspace?.workspaceType,
        };
      },
    );

    const clients = await Promise.all(clientByWorkspacePromises);

    // Ensure the filter function explicitly handles undefined values
    const filteredClients = clients.filter(
      (
        v,
      ): v is {
        workspaceClients: Client[];
        workspaceId: string | undefined;
        workspaceName: string;
        workspaceType: 'businessAgency';
      } =>
        v !== undefined &&
        v.workspaceClients !== undefined &&
        v.workspaceId !== undefined &&
        v.workspaceName !== undefined &&
        v.workspaceType === WorkspaceTypeEnum.BusinessAgency,
    );

    setUnconfirmedClientsByWorkspace(filteredClients as TUnconfirmedClients);

    setFetching(false);
  };

  useEffect(() => {
    if (!currentUser) return;
    // Only fetch data on Confirm Clients modal close
    if (showConfirmClientsModal) return;

    dispatch(reduxServices.workspace.findAll());
    getAllWorkspaceClients();
  }, [currentUser, location.state, workspaces, showConfirmClientsModal]);

  if (isLoadingState(isWorkspaceLoading) || fetching) {
    return <AppLoader.FlexLoader className="pt-9" />;
  }

  return (
    <>
      {unconfirmedClientsByWorkspace.map((client) => (
        <UnconfirmedClientsAlert
          key={String(client.workspaceId)}
          workspaceName={client.workspaceName ?? ''}
          onConfirm={() => {
            setReviewClients(client.workspaceClients as Client[]);
            setShowConfirmClientsModal(true);
            setSelectedWorkspace(String(client.workspaceId));
          }}
        />
      ))}

      <ConfirmClientsModal
        clients={reviewClients}
        isOpen={showConfirmClientsModal}
        workspaceId={String(selectedWorkspace)}
        onClientsChange={setReviewClients}
        onClose={() => {
          setReviewClients([]);
          setShowConfirmClientsModal(false);
          setSelectedWorkspace('');
        }}
      />
    </>
  );
};
